import type { InvoiceDatevConfig } from '../../types';
import {
    FreelancerCreditNote,
    FreelancerSnapshot,
    istKleinunternehmer,
} from '../freelancerCreditNoteLogic';
import { toCp1252Bytes } from './cp1252';
import {
    BUCHUNGSSTAPEL_COLUMNS_V13,
    CURRENCY_EUR,
    DatevExportOptions,
    DatevExportPeriod,
    DatevExportResult,
    FIELD_SEPARATOR,
    LINE_BREAK,
    MAX_LENGTH_BUCHUNGSTEXT,
    buildHeaderRow,
    datevPeriodLabel,
    formatAmountForDatev,
    formatDayMonth,
    formatDayMonthYear,
    formatYearMonthDay,
    quoteField,
    sanitizeDocumentNumber,
    sanitizeText,
    setField,
} from './datevExport';

/**
 * DATEV-Export der FREELANCER-GUTSCHRIFTEN (Phase D5) — EXTF Buchungsstapel,
 * Formatversion 700.
 *
 * ─── Warum ein eigener Exporter ────────────────────────────────────────────
 * `datevExport.ts` ist strikt DEBITORISCH verdrahtet: er nimmt `Invoice`,
 * bucht je Steuergruppe, holt das Konto aus `customerSnapshot.customerNumber`
 * und das Gegenkonto aus den ERLOESkonten je Steuersatz. Eine Gutschrift an
 * einen Freelancer ist die Gegenrichtung — Aufwand gegen Kreditor —, hat keine
 * `taxGroups` und keinen Kunden. Den Bestandsexporter dafuer umzubauen hiesse,
 * den einzigen Weg, auf dem heute Kundenrechnungen in die Kanzlei gehen, fuer
 * einen neuen Anwendungsfall anzufassen.
 *
 * GEMEINSAM bleibt das FORMAT: Kopfzeile, Spaltenliste, Feldbereinigung und
 * das Setzen ueber den Spaltennamen kommen aus `datevExport.ts` und sind dort
 * exportiert. Es gibt also genau EINE Formatfassung; wer sie tauscht, tauscht
 * sie fuer beide Exporter.
 *
 * ─── Die Buchung ──────────────────────────────────────────────────────────
 *   Konto        = Kreditor des Freelancers (freelancers.datev_kreditor_nr,
 *                  optional mit `creditorAccountBase` als Praefix)
 *   Gegenkonto   = `kreditorAufwandKonto` aus der DATEV-Konfiguration
 *   Umsatz       = BRUTTO (die Steuer steckt darin, DATEV rechnet sie ueber
 *                  den Steuerschluessel bzw. das Automatikkonto heraus)
 *   Soll/Haben   = folgt AUSSCHLIESSLICH dem Vorzeichen — der Storno traegt
 *                  negative Betraege und dreht die Seite dadurch von selbst.
 *                  Dieselbe Regel und dieselbe Begruendung wie im
 *                  Debitorenexport (dort ausfuehrlich kommentiert).
 *
 * ════════════════════════════════════════════════════════════════════════
 *  FORMAT BELEGT, KREDITORISCHES NICHT
 * ════════════════════════════════════════════════════════════════════════
 *  Das Satzformat ist ueber die Referenzdatei belegt (siehe Kopfkommentar in
 *  datevExport.ts). Ungeprueft bleibt alles Kreditorische — die Referenz
 *  enthaelt ausschliesslich Ausgangsrechnungen. Siehe
 *  UNVERIFIED_CREDIT_NOTE_DATEV_FIELDS am Dateiende.
 *
 *  Belegfeld 2 (Faelligkeit) und Beleglink bleiben hier bewusst leer: eine
 *  Freelancer-Gutschrift traegt kein Faelligkeitsdatum und kein archiviertes
 *  Beleg-PDF, aus dem sich eine GUID ableiten liesse.
 * ════════════════════════════════════════════════════════════════════════
 */

/** Belege ohne Nummer sind Entwuerfe und werden hart abgewiesen, nicht still
 *  uebersprungen — ein stiller Sprung faellt erst beim Kontenabgleich auf. */
const exportierbar = (b: FreelancerCreditNote): boolean =>
    b.status !== 'entwurf' && !!b.nummer;

/**
 * Kreditorenkonto aus dem eingefrorenen Snapshot.
 *
 * BEWUSST aus dem Snapshot und nicht aus dem Live-Stammsatz: der Beleg ist
 * festgeschrieben, und eine spaeter geaenderte Kreditorennummer wuerde einen
 * bereits gebuchten Beleg auf ein anderes Konto legen.
 */
export const kreditorKonto = (
    snapshot: FreelancerSnapshot | null | undefined,
    config: InvoiceDatevConfig,
    belegNummer: string,
): string => {
    const nr = (snapshot?.datevKreditorNr || '').trim();
    if (!nr) {
        throw new Error(
            `Gutschrift ${belegNummer}: im Freelancer-Stammsatz ist keine DATEV-`
                + 'Kreditorennummer hinterlegt. Ohne sie ist keine Buchung möglich.',
        );
    }
    const konto = `${config.creditorAccountBase ?? ''}${nr}`;
    if (!/^\d+$/.test(konto)) {
        throw new Error(
            `Gutschrift ${belegNummer}: Kreditorenkonto "${konto}" ist nicht rein numerisch.`,
        );
    }
    return konto;
};

/** Aufwandskonto. Wirft, statt eines zu raten — dieselbe Regel wie bei den
 *  Erloeskonten des Debitorenexports. */
export const aufwandsKonto = (config: InvoiceDatevConfig): string => {
    const konto = (config.kreditorAufwandKonto || '').trim();
    if (!konto) {
        throw new Error(
            'Kein Aufwandskonto für Freelancer-Gutschriften konfiguriert. '
                + 'Bitte in den DATEV-Einstellungen hinterlegen.',
        );
    }
    return konto;
};

/**
 * Buchungstext: Name des Freelancers und der Leistungszeitraum.
 *
 * Der Name kommt aus dem SNAPSHOT — er steht auch auf dem Beleg, und die
 * Buchung soll den Beleg wiedergeben, nicht den heutigen Stammsatz.
 */
export const buchungsText = (beleg: FreelancerCreditNote): string => {
    const s = beleg.freelancerSnapshot || {};
    const name = (s.firma || s.name || '').trim();
    const zeitraum = beleg.leistungszeitraumBis
        ? ` ${beleg.leistungszeitraumBis.slice(5, 7)}/${beleg.leistungszeitraumBis.slice(0, 4)}`
        : '';
    const praefix = beleg.stornoOf ? 'Storno Gutschrift ' : 'Gutschrift ';
    return sanitizeText(`${praefix}${name}${zeitraum}`, MAX_LENGTH_BUCHUNGSTEXT);
};

/**
 * EINE Buchungszeile je Beleg.
 *
 * Anders als im Debitorenexport gibt es hier keine Aufteilung je Steuergruppe:
 * eine Gutschrift traegt genau EINEN Steuersatz (den des Freelancers), der als
 * `steuersatz_bp` am Beleg eingefroren ist. Zwei Saetze auf einem Beleg gibt
 * es fachlich nicht.
 *
 * Exportiert und rein: sie bekommt alles als Argument und liest keinen
 * Zustand — genau das macht sie einzeln pruefbar.
 */
export const buildCreditNoteBookingRow = (
    beleg: FreelancerCreditNote,
    config: InvoiceDatevConfig,
): string => {
    const row: string[] = new Array(BUCHUNGSSTAPEL_COLUMNS_V13.length).fill('');
    const nummer = beleg.nummer ?? '';
    const brutto = beleg.bruttoCents;

    // Die Richtung folgt allein dem Vorzeichen. Der Aufwand steht im SOLL;
    // der Storno traegt negative Betraege und landet dadurch im HABEN, hebt
    // die Buchung also auf, statt sie zu verdoppeln.
    const habenSeite = brutto < 0;

    setField(row, 'Umsatz', formatAmountForDatev(brutto), false);
    setField(row, 'Soll-/Haben-Kennzeichen', habenSeite ? 'H' : 'S', true);
    setField(row, 'WKZ Umsatz', CURRENCY_EUR, true);
    // Konto = Aufwand, Gegenkonto = Kreditor. Der Aufwand ist die Sicht, aus
    // der gebucht wird; das Kreditorenkonto ist die Gegenseite.
    setField(row, 'Konto', aufwandsKonto(config), false);
    setField(
        row,
        'Gegenkonto (ohne BU-Schlüssel)',
        kreditorKonto(beleg.freelancerSnapshot, config, nummer),
        false,
    );
    setField(row, 'Belegdatum', formatDayMonth(beleg.ausgestelltAm, 'Belegdatum'), false);
    setField(row, 'Belegfeld 1', sanitizeDocumentNumber(nummer), true);
    setField(row, 'Buchungstext', buchungsText(beleg), true);
    // Festschreibung immer '1': ein nicht festgeschriebener Stapel ist in der
    // Kanzlei nachtraeglich aenderbar und damit nicht GoBD-konform.
    setField(row, 'Festschreibung', '1', false);

    if (beleg.leistungszeitraumBis) {
        setField(
            row,
            'Leistungsdatum',
            formatDayMonthYear(beleg.leistungszeitraumBis, 'Leistungsdatum'),
            false,
        );
    }

    // Beim Kleinunternehmer (§19) enthaelt der Betrag KEINE Vorsteuer. Der
    // Steuersatz wird deshalb ausdruecklich als 0 mitgegeben, damit ein
    // Automatikkonto nicht doch 19 % herausrechnet — das waere ein
    // unberechtigter Vorsteuerabzug.
    setField(
        row,
        'Steuersatz',
        istKleinunternehmer(beleg.steuersatzBp)
            ? '0,00'
            : (beleg.steuersatzBp / 100).toFixed(2).replace('.', ','),
        false,
    );

    return row.join(FIELD_SEPARATOR);
};

/**
 * Baut den EXTF-Buchungsstapel der Gutschriften als CP1252-Bytes.
 *
 * Entwuerfe fuehren zum ABBRUCH, nicht zum Ueberspringen — gleiche
 * Begruendung wie im Debitorenexport.
 */
export const exportCreditNotesDatev = (
    belege: FreelancerCreditNote[],
    config: InvoiceDatevConfig | undefined,
    period: DatevExportPeriod,
    options: DatevExportOptions = {},
): DatevExportResult => {
    if (!config) {
        throw new Error(
            'Keine DATEV-Konfiguration hinterlegt. Beraternummer, Mandantennummer und '
                + 'das Aufwandskonto für Freelancer-Gutschriften sind für den Export zwingend.',
        );
    }

    const entwuerfe = (belege || []).filter((b) => !exportierbar(b));
    if (entwuerfe.length > 0) {
        throw new Error(
            'Nicht ausgestellte Gutschriften können nicht nach DATEV exportiert werden: '
                + entwuerfe.map((b) => b.nummer ?? `Entwurf ${b.id}`).join(', ')
                + '. Bitte zuerst ausstellen oder aus dem Zeitraum ausnehmen.',
        );
    }

    const rows = (belege || []).map((b) => buildCreditNoteBookingRow(b, config));

    const zeilen = [
        buildHeaderRow(config, period, {
            ...options,
            // "GS" statt "Gutschriften": die Bezeichnung (Feld 17) fasst nur
            // 30 Zeichen, und "Gutschriften Freelancer 2026-08-01 bis ..."
            // wurde mitten im Wort gekappt. Laengste Form jetzt:
            // "GS Freelancer 12/25-01/26" (25 Zeichen).
            description: options.description ?? `GS Freelancer ${datevPeriodLabel(period)}`,
        }),
        BUCHUNGSSTAPEL_COLUMNS_V13.map(quoteField).join(FIELD_SEPARATOR),
        ...rows,
    ];

    // Abschliessendes CRLF: die letzte Buchungszeile wird wie jede andere
    // beendet, sonst faellt sie bei manchen Importern unter den Tisch.
    const inhalt = `${zeilen.join(LINE_BREAK)}${LINE_BREAK}`;

    const fileName =
        `EXTF_Gutschriften_${formatYearMonthDay(period.from, 'Zeitraum von')}_`
        + `${formatYearMonthDay(period.to, 'Zeitraum bis')}.csv`;

    return { fileName, bytes: toCp1252Bytes(inhalt) };
};

/**
 * Die offenen Punkte DIESES Exporters, zusaetzlich zu denen aus
 * datevExport.ts (UNVERIFIED_DATEV_FIELDS). Gedacht als Quelle fuer den
 * Warnhinweis im Export-Dialog, solange der Steuerberater keinen Probeimport
 * bestaetigt hat.
 */
export const UNVERIFIED_CREDIT_NOTE_DATEV_FIELDS: string[] = [
    'Buchungsrichtung: Konto = Aufwand, Gegenkonto = Kreditor. Ob die Kanzlei die umgekehrte Belegung erwartet, ist offen.',
    'Kreditorenkonto: ob creditorAccountBase ein Präfix oder ein Startwert ist und welche Stellenzahl DATEV erwartet, ist offen — gleiche Unsicherheit wie beim Debitorenkonto.',
    'Der BU-Schlüssel bleibt leer; der Steuersatz wird stattdessen im Feld "Steuersatz" mitgegeben. Ob die Kanzlei mit Automatikkonten arbeitet oder einen Steuerschlüssel erwartet, ist zu bestätigen.',
    'Kleinunternehmer (§19 UStG): Steuersatz wird als 0,00 gesetzt, damit kein Automatikkonto Vorsteuer herausrechnet. Mit der Kanzlei bestätigen.',
    'Das Gutschriftverfahren nach §14 Abs. 2 Satz 2 UStG ist buchhalterisch eine Eingangsrechnung. Ob die Kanzlei dafür einen eigenen Belegkreis führen möchte, ist zu klären.',
];
